import OfferStatusBadge from '@/components/molecules/offerStatusBadge';
import { ApplicationState } from '@/store';
import {
  exemptApplicantOfferPayment,
  exemptApplicantInterviewRequirements,
  exemptApplicantStaffRequirements,
  getApplicantOffer,
  studentsPinTag,
  studentsUnpinTag,
} from '@/store/apply/actions';
import { getRole } from '@/store/selectors';
import { formatYearMonthDate } from '@/utilities/dateHelpers';
import { getApplicationOfferBadgeProps } from '@/utilities/getApplicationOfferBadgeProps';
import { hasPerm, P } from '@/utilities/roles/permissions';
import {
  groupRequirementsBySection,
  SectionGroup,
  TransformedRequirementTask,
  transformRequirements,
} from '@/utilities/transformRequirements';
import { getFeatureFlags } from '@/store/config/selectors';
import { Badge, Box, Flex, useDisclosure } from '@chakra-ui/react';
import { faEdit, faFilePlus, faPen } from '@fortawesome/pro-regular-svg-icons';
import { OfferTooltipLabels } from 'data';
import { JSX, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router-dom';
import {
  CustomHeading,
  getStatusByConditionType,
  IconButtonWithTooltip,
  RoundBadgeWithTooltip,
  Span,
  SpinnerModal,
} from 'shared-components';
import {
  AgentApplicantAuthorisationStatus,
  ApplicantOfferRequirement,
  UniConfigRequirementTaskFulfilmentMethod,
} from 'types';
import ApplicantHeader from '../../../molecules/applicantHeader';
import StudentHeaderTags from '../../../molecules/studentHeader/Tags';
import ProgrammeDetailsModal from '../programmes/ProgrammeDetailsModal';
import { AddRequirement } from './AddRequirement';
import RequirementCogMenu from './RequirementCogMenu';
import { OfferLetterText } from './OfferLetterText';
import { OtherQualifications } from './OtherQualifications';
import { PaymentDeposit, PaymentProof } from './Payment';
import RequirementRow from './RequirementRow';
import { useTranslation } from "react-i18next";

const INTERVIEWS_SECTION_KEY = 'Interviews';

const hideUploadButton: UniConfigRequirementTaskFulfilmentMethod[] = [
  UniConfigRequirementTaskFulfilmentMethod.AcademicResults,
  UniConfigRequirementTaskFulfilmentMethod.EnglishLanguageResults,
  UniConfigRequirementTaskFulfilmentMethod.CalendlyInterview,
  UniConfigRequirementTaskFulfilmentMethod.RecordedInterview,
  UniConfigRequirementTaskFulfilmentMethod.RefereeDetails,
];

const sectionPaths: Partial<Record<ApplicantOfferRequirement, string>> = {
  [ApplicantOfferRequirement.Identity]: 'identity',
  [ApplicantOfferRequirement.AcademicRequirement]: 'education-history',
  [ApplicantOfferRequirement.EnglishLanguageRequirement]: 'english-language',
  [ApplicantOfferRequirement.Employment]: 'employment',
  [ApplicantOfferRequirement.ImmigrationHistory]: 'immigration-history',
  [ApplicantOfferRequirement.References]: 'references',
  [ApplicantOfferRequirement.ResearchProposal]: 'research-proposal',
};

const sectionNameLookup: Partial<Record<ApplicantOfferRequirement, string>> & Record<string, string> = {
  [ApplicantOfferRequirement.Identity]: 'Identity',
  [ApplicantOfferRequirement.AcademicRequirement]: 'Academic Requirements',
  [ApplicantOfferRequirement.EnglishLanguageRequirement]: 'English Language',
  [ApplicantOfferRequirement.Employment]: 'Employment History',
  [ApplicantOfferRequirement.ImmigrationHistory]: 'Immigration History',
  [ApplicantOfferRequirement.References]: 'References',
  [ApplicantOfferRequirement.ResearchProposal]: 'Research Proposal',
  [ApplicantOfferRequirement.Portfolio]: 'Portfolio',
  [INTERVIEWS_SECTION_KEY]: 'Interviews',
  [ApplicantOfferRequirement.Finance]: 'Finance',
  [ApplicantOfferRequirement.Custom]: 'Custom Requirements',
  [ApplicantOfferRequirement.StaffOnly]: 'Staff-only Requirements',
};

// Defines the display order for requirement sections on the Programme page.
// Sections not in this map will appear next to the end (before Custom).
const UNKNOWN_SECTION_SORT_ORDER = 99;
const sectionSortOrder: Partial<Record<ApplicantOfferRequirement, number>> & Record<string, number> = {
  [ApplicantOfferRequirement.AcademicRequirement]: 1,
  [ApplicantOfferRequirement.EnglishLanguageRequirement]: 2,
  [ApplicantOfferRequirement.Identity]: 3,
  [ApplicantOfferRequirement.Employment]: 4,
  [ApplicantOfferRequirement.References]: 5,
  [ApplicantOfferRequirement.Portfolio]: 6,
  [ApplicantOfferRequirement.ResearchProposal]: 7,
  [ApplicantOfferRequirement.ImmigrationHistory]: 8,
  [INTERVIEWS_SECTION_KEY]: 9,
  [ApplicantOfferRequirement.Finance]: 10,
  [ApplicantOfferRequirement.Custom]: 100,
  [ApplicantOfferRequirement.StaffOnly]: 101,
};

export default function Offers(): JSX.Element {
    const { t } = useTranslation('common');
  const { uploadedSuccessfully } = useSelector((state: ApplicationState) => state.student.documents);

  const role = useSelector(getRole);
  const featureFlags = useSelector(getFeatureFlags);
  const staffRequirementsEnabled = featureFlags?.STAFF_REQUIREMENTS_ENABLED === true;

  const params = useParams();
  const { userId, offerId: offerIdParam, uni } = params;
  const { application, agentAccessStatus, loadingOffer, offerError } = useSelector(
    (state: ApplicationState) => state.apply,
  );
  const offer = useSelector((state: ApplicationState) => state.apply.offer);
  const offerId = offerIdParam ?? offer?.id;
  const requirementsConditionTexts = useSelector((state: ApplicationState) => state.apply.requirementsConditionTexts);
  const tags = offer?.tags;
  const allTags = useSelector((state: ApplicationState) => state.config.tags);
  const dispatch = useDispatch();
  const {
    onOpen: onProgrammeDetailsModalOpen,
    isOpen: isProgrammeDetailsModalOpened,
    onClose: onProgrammeDetailsModalClose,
  } = useDisclosure();

  const fetchOffer = (): void => {
    dispatch(getApplicantOffer(userId, uni, offerId));
  };

  const pinTag = ({
    tags: tagList,
  }: {
    tags: Array<{ id: string; isProgrammable: boolean }>;
    studentNumbers: string[];
  }): void => {
    const [tag] = tagList;
    const hasTag = tags?.some(({ id }) => tag.id === id);
    if (hasTag) return;
    dispatch(studentsPinTag(offerId, application.userId, tag));
  };

  const unPinTag = ({
    tags: tagList,
  }: {
    tags: Array<{ id: string; isProgrammable: boolean }>;
    studentNumbers: string[];
  }): void => {
    const [tag] = tagList;
    dispatch(studentsUnpinTag(offerId, application.userId, tag));
  };

  useEffect(() => {
    fetchOffer();
  }, [offerId]);

  // Refetch offer data when document upload is successful
  useEffect(() => {
    if (uploadedSuccessfully) {
      fetchOffer();
    }
  }, [uploadedSuccessfully]);

  // Transform hierarchical requirements to flat tasks with parent metadata
  const transformedTasks = useMemo(() => {
    if (!offer?.requirements) {
      return [];
    }
    return transformRequirements(offer.requirements);
  }, [offer?.requirements]);

  const buildSectionGroups = (tasks: TransformedRequirementTask[], isStaff: boolean): SectionGroup[] => {
    if (!tasks.length) return [];

    // Group tasks by sectionId (maps to ApplicantOfferRequirement enum) so that
    // multiple academic requirement groups still appear under one "Academic Requirements" section.
    // RecordedInterview and CalendlyInterview will show combined under 'Interviews' section
    const sectionGroups: Record<string, TransformedRequirementTask[]> = {};
    for (const task of tasks) {
      const isInterview =
        task.sectionId === ApplicantOfferRequirement.RecordedInterview ||
        task.sectionId === ApplicantOfferRequirement.CalendlyInterview;
      const section = isInterview ? INTERVIEWS_SECTION_KEY : (task.sectionId ?? task.requirementId);
      if (!sectionGroups[section]) {
        sectionGroups[section] = [];
      }
      sectionGroups[section].push(task);
    }

    return Object.entries(sectionGroups)
      .sort(([sectionA], [sectionB]) => {
        const orderA = sectionSortOrder[sectionA as ApplicantOfferRequirement] ?? UNKNOWN_SECTION_SORT_ORDER;
        const orderB = sectionSortOrder[sectionB as ApplicantOfferRequirement] ?? UNKNOWN_SECTION_SORT_ORDER;
        return orderA - orderB;
      })
      .map(([section, sectionTasks]) => {
        const isInterviewSection = section === INTERVIEWS_SECTION_KEY;
        return {
          sectionName: isStaff ? '' : (sectionNameLookup[section] ?? section),
          sectionPath: isStaff ? '' : (sectionPaths[section] ?? ''),
          requirementGroups: groupRequirementsBySection(
            sectionTasks,
            isInterviewSection ? INTERVIEWS_SECTION_KEY : undefined,
          ),
        };
      });
  };

  const requirementsBySection = useMemo((): SectionGroup[] => {
    const applicantTasks = transformedTasks.filter(t => t.sectionId !== ApplicantOfferRequirement.StaffOnly);
    return buildSectionGroups(applicantTasks, false);
  }, [transformedTasks]);

  const staffRequirementsBySection = useMemo((): SectionGroup[] => {
    const staffTasks = transformedTasks.filter(t => t.sectionId === ApplicantOfferRequirement.StaffOnly);
    return buildSectionGroups(staffTasks, true);
  }, [transformedTasks]);

  const previousStudies = application?.studyHistory?.previous;

  const hasRequirementsBeenEdited = transformedTasks.some(
    req => Object.keys(req?.pendingChanges ?? {}).length > 0 && !req.lastUpdatedByApplicant,
  );
  const hasBeenEdited = Object.keys(offer?.pendingChanges ?? {}).length > 0 || hasRequirementsBeenEdited;
  const hasBeenEditedByApplicant = transformedTasks.some(
    req => Object.keys(req?.pendingChanges ?? {}).length > 0 && req.lastUpdatedByApplicant,
  );

  const badgeProps = getApplicationOfferBadgeProps(offer?.status);
  const applicantStatus = getStatusByConditionType(offer?.applicantStatus, offer?.conditionType);

  const isAuthorisationDenied = agentAccessStatus === AgentApplicantAuthorisationStatus.AUTHORISATION_DENIED;

  const canEditOffer = hasPerm(role, P.EDIT_APPLY_OFFER) && !isAuthorisationDenied;
  const canEditTags = hasPerm(role, P.EDIT_APPLY_TAGS) && !isAuthorisationDenied;

  const entryPoint = offer?.pendingChanges?.programme?.entryPoint ?? offer?.programme?.entryPoint;

  if (loadingOffer && !offer) {
    return <SpinnerModal show={true} />;
  }

  if (offerError || !offer) {
    throw new Error(offerError || 'Offer not found');
  }

  return (
    <>
      <ApplicantHeader showProgrammesCrumb pageName={offer?.programme?.courseName}>
        <Box mb={6}>
          <Flex mb={2} columnGap={1} alignItems="center">
            <CustomHeading data-selector="offersHeader-name" level="h1" flexGrow={1} mb={0} wordBreak={'break-word'}>
              {offer?.programme?.courseName}
            </CustomHeading>
          </Flex>

          <Flex mt={2} justifyContent="space-between" alignItems="center" gap={2}>
            <Flex alignItems="center" gap={1} flexWrap="wrap">
              <Badge colorScheme={badgeProps.colorScheme} size="md" data-selector="offer-status-badge">
                {badgeProps.children}
              </Badge>
              <OfferStatusBadge applicantStatus={applicantStatus} />
              {hasBeenEdited && (
                <RoundBadgeWithTooltip
                  icon={faPen}
                  label={OfferTooltipLabels.pendingChanges}
                  colorScheme="orange"
                  size="md"
                />
              )}
              {hasBeenEditedByApplicant && (
                <RoundBadgeWithTooltip
                  icon={faFilePlus}
                  label={OfferTooltipLabels.pendingChangesByApplicant}
                  colorScheme="indigo"
                  size="md"
                />
              )}
              <Span color="muted">
                {offer?.programme?.mode}
                {offer?.programme?.campus && `, ${offer.programme.campus}`},{' '}
                {formatYearMonthDate(offer?.intakeId, true)}
                {entryPoint && `, ${entryPoint} entry`}
              </Span>
            </Flex>
            {canEditOffer && (
              <IconButtonWithTooltip
                label={t('common:changeProgrammeDetails')}
                iconProps={{ icon: faEdit }}
                buttonProps={{ size: 'sm' }}
                onClick={onProgrammeDetailsModalOpen}
              />
            )}
          </Flex>

          <StudentHeaderTags
            role={role}
            tags={tags}
            isPathway={false}
            studentNo={application.userId}
            university={uni}
            intakeId={offer.intakeId}
            offerId={offer.id}
            unpinTagsFromStudents={unPinTag}
            pinTagsToStudents={pinTag}
            allProgrammableTags={[]}
            allTags={allTags}
            disableEdit={!canEditTags}
          />
        </Box>
      </ApplicantHeader>
      <OfferLetterText type="startText" />
      <Box>
        {requirementsBySection?.map((section, sectionIndex) => (
          <Box mt={6} key={sectionIndex}>
            {section.requirementGroups.map((group, groupIndex) => {
              const isInterviewGroup = group.requirementId === INTERVIEWS_SECTION_KEY;
              // CalendlyInterview and RecordedInterview tasks are grouped under a synthetic
              // 'Interviews' key, so we collect unique sectionIds and exempt them sequentially
              // in a single saga to avoid race conditions on the shared offer state.
              return (
                <Box key={groupIndex} mb={4}>
                  <Flex alignItems="center" justifyContent="space-between" py={2}>
                    <CustomHeading as="h3" level="h5" fontWeight="semibold" mb={2}>
                      {group.requirementText || section.sectionName}
                    </CustomHeading>
                    {canEditOffer &&
                      (isInterviewGroup ? (
                        <RequirementCogMenu
                          applicantId={userId}
                          uni={uni}
                          offerId={offerId}
                          onExempt={(noteText: string): void => {
                            const requirementIds = [
                              ...new Set(group.tasks.map(t => t.sectionId).filter(Boolean)),
                            ] as string[];
                            dispatch(
                              exemptApplicantInterviewRequirements({
                                applicantId: userId,
                                uni,
                                offerId,
                                requirementIds,
                                noteText: noteText || undefined,
                              }),
                            );
                          }}
                          hasTasks={group.tasks.length > 0}
                        />
                      ) : (
                        <RequirementCogMenu
                          requirementId={group.requirementId}
                          applicantId={userId}
                          uni={uni}
                          offerId={offerId}
                          hasTasks={group.tasks.length > 0}
                        />
                      ))}
                  </Flex>
                  <Box>
                    {group.tasks.map((req, rIndex) => (
                      <RequirementRow
                        requirement={req}
                        key={`${offerId}-${req.id}`}
                        userId={userId}
                        uni={uni}
                        offerId={offerId}
                        index={rIndex}
                        requirementsConditionTexts={requirementsConditionTexts}
                        disableEdit={!canEditOffer}
                        hideUploadButton={hideUploadButton.includes(req.fulfilmentMethod)}
                        requirementText={group.requirementText}
                        offerStatus={offer?.status}
                        applicationId={offer?.applicationId}
                      />
                    ))}
                  </Box>
                  {section.sectionPath === 'education-history' && previousStudies && (
                    <OtherQualifications previousStudies={previousStudies} />
                  )}
                </Box>
              );
            })}
          </Box>
        ))}
      </Box>

      {offer.payment && (
        <Box my={6}>
          <Flex alignItems="center" justifyContent="space-between" py={2}>
            <CustomHeading as="h3" level="h5" fontWeight="semibold" mb={2}>
              {t('common:finance')}
                                      </CustomHeading>
            {canEditOffer && (
              <RequirementCogMenu
                applicantId={userId}
                uni={uni}
                offerId={offerId}
                onExempt={noteText =>
                  dispatch(
                    exemptApplicantOfferPayment({
                      applicantId: userId,
                      uni,
                      offerId,
                      noteText: noteText || undefined,
                    }),
                  )
                }
              />
            )}
          </Flex>
          <Box>
            <PaymentDeposit payment={offer.payment} userId={userId} uni={uni} offerId={offerId} />
            <PaymentProof payment={offer.payment} userId={userId} uni={uni} offerId={offerId} />
          </Box>
        </Box>
      )}

      {canEditOffer && <AddRequirement />}

      {staffRequirementsEnabled && (
        <Box mt={8}>
          <Flex alignItems="center" justifyContent="space-between" py={2}>
            <CustomHeading as="h3" level="h4" fontWeight="medium" mb={0}>
              {t('common:staffOnlyRequirements')}
                                      </CustomHeading>
            {canEditOffer && (
              <RequirementCogMenu
                applicantId={userId}
                uni={uni}
                offerId={offerId}
                hasTasks={staffRequirementsBySection?.some(section =>
                  section.requirementGroups.some(group => group.tasks.length > 0),
                )}
                onExempt={noteText => {
                  const requirementIds = (staffRequirementsBySection ?? [])
                    .flatMap(section => section.requirementGroups.map(group => group.requirementId))
                    .filter(Boolean);
                  dispatch(
                    exemptApplicantStaffRequirements({
                      applicantId: userId,
                      uni,
                      offerId,
                      requirementIds,
                      noteText: noteText || undefined,
                    }),
                  );
                }}
              />
            )}
          </Flex>
          {staffRequirementsBySection?.map((section, sectionIndex) => (
            <Box mt={6} key={sectionIndex}>
              {section.requirementGroups.map((group, groupIndex) => (
                <Box key={groupIndex} mb={4}>
                  <Box>
                    {group.tasks.map((req, rIndex) => (
                      <RequirementRow
                        requirement={req}
                        key={`${offerId}-${req.id}`}
                        userId={userId}
                        uni={uni}
                        offerId={offerId}
                        index={rIndex}
                        requirementsConditionTexts={requirementsConditionTexts}
                        disableEdit={!canEditOffer}
                        hideUploadButton={hideUploadButton.includes(req.fulfilmentMethod)}
                        requirementText={group.requirementText}
                        offerStatus={offer?.status}
                        applicationId={offer?.applicationId}
                      />
                    ))}
                  </Box>
                </Box>
              ))}
            </Box>
          ))}
          {canEditOffer && <AddRequirement staffOnly={true} />}
        </Box>
      )}
      <OfferLetterText type="endText" />

      {isProgrammeDetailsModalOpened && canEditOffer && (
        <ProgrammeDetailsModal onClose={onProgrammeDetailsModalClose} />
      )}
    </>
  );
}
